import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Package } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { imgSrc } from "@/lib/img";
import { Card, PageTitle, Empty, Select } from "@/admin/ui";

const STATUSES = ["placed", "confirmed", "shipped", "delivered", "cancelled"];
const PAY = ["pending", "paid", "refunded"];
const STATUS_CLS = { placed: "bg-[#FBF6E8] text-gold", confirmed: "bg-[#E8F0EA] text-forest", shipped: "bg-blue-50 text-blue-700", delivered: "bg-forest text-cream", cancelled: "bg-red-50 text-red-700" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

const OrderRow = ({ order, onChange }) => {
  const [open, setOpen] = useState(false);
  const update = async (patch) => {
    try {
      const { data } = await adminApi.patch(`/admin/orders/${order.id}`, patch);
      onChange(data);
      toast.success("Order updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <li data-testid={`admin-order-${order.order_number}`} className="px-4 sm:px-6 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} data-testid={`order-toggle-${order.order_number}`} className="flex items-center gap-2 font-mono text-sm font-bold text-forest">
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />} {order.order_number}
        </button>
        <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${STATUS_CLS[order.status]}`}>{order.status}</span>
        <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${order.payment_status === "paid" ? "bg-[#E8F0EA] text-forest" : "bg-creamdark text-mutedgreen"}`}>{order.payment_status}</span>
        <span className="text-xs text-mutedgreen flex-1 min-w-0 truncate">{order.customer.name} · {order.customer.city} · {fmt(order.created_at)}</span>
        <span data-testid={`order-total-${order.order_number}`} className="font-semibold text-forest">₹{order.total}</span>
      </div>
      {open && (
        <div className="mt-4 grid lg:grid-cols-[1.4fr_1fr] gap-5">
          <div className="space-y-3">
            <ul className="divide-y divide-forest/5 bg-cream/60 rounded-xl">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <img src={imgSrc(i.image)} alt="" className="w-9 h-9 rounded-lg object-cover" />
                  <span className="flex-1 text-forest">{i.name} <span className="text-mutedgreen">{i.variant_label ? `· ${i.variant_label} ` : ""}× {i.qty}</span></span>
                  <span className="font-semibold text-forest">₹{i.line_total}</span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-mutedgreen space-y-0.5 px-1">
              <p>Subtotal ₹{order.subtotal}{order.discount > 0 && ` · Discount −₹${order.discount} (${order.coupon_code})`} · Shipping {order.shipping > 0 ? `₹${order.shipping}` : "Free"}</p>
              {order.customer.notes && <p>Note: {order.customer.notes}</p>}
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="bg-cream/60 rounded-xl p-3">
              <p className="font-semibold text-forest">{order.customer.name}</p>
              <p className="text-mutedgreen">{order.customer.address}, {order.customer.city}, {order.customer.state} – {order.customer.pincode}</p>
              <p className="text-mutedgreen"><a href={`tel:${order.customer.phone}`} className="hover:text-forest">{order.customer.phone}</a> · <a href={`mailto:${order.customer.email}`} className="hover:text-forest">{order.customer.email}</a></p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-1">Order status</span>
                <Select data-testid={`order-status-${order.order_number}`} value={order.status} onChange={(e) => update({ status: e.target.value })}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></label>
              <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-1">Payment</span>
                <Select data-testid={`order-payment-${order.order_number}`} value={order.payment_status} onChange={(e) => update({ payment_status: e.target.value })}>{PAY.map((s) => <option key={s} value={s}>{s}</option>)}</Select></label>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { adminApi.get("/admin/orders", { params: { status: filter } }).then(({ data }) => setOrders(data)).catch(() => {}); }, [filter]);
  const onChange = (o) => setOrders((list) => list.map((x) => (x.id === o.id ? o : x)));

  return (
    <div data-testid="admin-orders-page">
      <PageTitle title="Orders" sub="Every order placed on the website. Update status as you confirm, ship and deliver." action={
        <Select data-testid="orders-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="!w-44"><option value="all">All orders</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
      } />
      <Card className="overflow-hidden">
        {orders.length === 0 && <Empty text={<span className="inline-flex items-center gap-2"><Package size={16} /> No orders yet.</span>} />}
        <ul className="divide-y divide-forest/10">{orders.map((o) => <OrderRow key={o.id} order={o} onChange={onChange} />)}</ul>
      </Card>
    </div>
  );
}
